// src/services/identity.js
// ─────────────────────────────────────────────────────────────────────────────
// Supabase-backed identity service. Syncs the player's Pazurumon identity
// to the cloud so it can be recovered on other devices / future games.
//
// Table required in Supabase (Leaderboard project):
//
//   CREATE TABLE player_identities (
//     id          UUID PRIMARY KEY DEFAULT gen_random_uuid(),
//     player_id   TEXT UNIQUE NOT NULL,
//     display_name TEXT NOT NULL,
//     selections  JSONB NOT NULL,
//     year        TEXT,
//     country_code TEXT DEFAULT 'XX',
//     discord     TEXT,
//     link_code   TEXT UNIQUE NOT NULL,
//     created_at  TIMESTAMPTZ DEFAULT now(),
//     updated_at  TIMESTAMPTZ DEFAULT now()
//   );
//
//   -- RLS: allow anon read by link_code, allow anon insert/update by player_id
//   ALTER TABLE player_identities ENABLE ROW LEVEL SECURITY;
//   CREATE POLICY "anon_read"   ON player_identities FOR SELECT USING (true);
//   CREATE POLICY "anon_insert" ON player_identities FOR INSERT WITH CHECK (true);
//   CREATE POLICY "anon_update" ON player_identities FOR UPDATE USING (true);
// ─────────────────────────────────────────────────────────────────────────────

const BASE = 'https://bnwakofsuhnglavdwmin.supabase.co/rest/v1';
const KEY  = 'sb_publishable_mdJ0aJ7gQhe7ju3m5kr_MA_a55EaJlv';

const HEADERS = {
  'apikey':        KEY,
  'Authorization': `Bearer ${KEY}`,
  'Content-Type':  'application/json',
};

const PLAYER_ID_KEY = 'pzm_player_id';

// ── Generate / retrieve persistent player UUID ────────────────────────────

export function getPlayerId() {
  let id = localStorage.getItem(PLAYER_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(PLAYER_ID_KEY, id);
  }
  return id;
}

// ── Generate a 6-char alphanumeric link code ──────────────────────────────

function generateLinkCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no 0/O/1/I for clarity
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

// ── Save identity to Supabase (upsert by player_id) ──────────────────────

export async function saveIdentity(identity) {
  const playerId = getPlayerId();
  const linkCode = identity.linkCode || generateLinkCode();

  const payload = {
    player_id:    playerId,
    display_name: identity.displayName,
    selections:   identity.selections,
    year:         identity.year || null,
    country_code: identity.countryCode || 'XX',
    discord:      identity.discord || null,
    link_code:    linkCode,
    updated_at:   new Date().toISOString(),
  };

  try {
    // Try upsert (requires ON CONFLICT on player_id)
    const r = await fetch(`${BASE}/player_identities`, {
      method: 'POST',
      headers: {
        ...HEADERS,
        'Prefer': 'resolution=merge-duplicates,return=representation',
      },
      body: JSON.stringify(payload),
    });

    if (r.ok) {
      const [row] = await r.json();
      // Persist link code locally
      localStorage.setItem('pzm_link_code', row?.link_code || linkCode);
      return { ok: true, linkCode: row?.link_code || linkCode };
    }

    return { ok: false, error: 'server_error' };
  } catch {
    return { ok: false, error: 'network_error' };
  }
}

// ── Recover identity by link code ─────────────────────────────────────────

export async function recoverIdentity(linkCode) {
  const code = linkCode.trim().toUpperCase();
  if (code.length !== 6) return { ok: false, error: 'invalid_code' };

  try {
    const r = await fetch(
      `${BASE}/player_identities?link_code=eq.${code}&select=*&limit=1`,
      { headers: HEADERS }
    );

    if (!r.ok) return { ok: false, error: 'server_error' };

    const rows = await r.json();
    if (rows.length === 0) return { ok: false, error: 'not_found' };

    const row = rows[0];

    // Adopt this identity locally
    localStorage.setItem(PLAYER_ID_KEY, row.player_id);
    localStorage.setItem('pzm_link_code', row.link_code);
    localStorage.setItem('pzm_country', row.country_code || 'XX');
    localStorage.setItem('pzm_player_name', row.display_name);
    localStorage.setItem('pzm_identity', JSON.stringify({
      selections:  row.selections,
      year:        row.year,
      countryCode: row.country_code,
      discord:     row.discord,
      linkCode:    row.link_code,
      createdAt:   row.created_at,
    }));

    return { ok: true, identity: row };
  } catch {
    return { ok: false, error: 'network_error' };
  }
}

// ── Fetch own identity from cloud (e.g. on boot to check for updates) ─────

export async function fetchOwnIdentity() {
  const playerId = localStorage.getItem(PLAYER_ID_KEY);
  if (!playerId) return null;

  try {
    const r = await fetch(
      `${BASE}/player_identities?player_id=eq.${playerId}&select=*&limit=1`,
      { headers: HEADERS }
    );
    if (!r.ok) return null;
    const rows = await r.json();
    return rows[0] || null;
  } catch {
    return null;
  }
}
